import { useMemo, useState } from 'react'

export interface Series {
  key: string
  label: string
  color: string
  values: number[]
}

interface ChartProps {
  labels: string[]
  series: Series[]
  height?: number
  yLabel?: string
}

/**
 * Stacked daily bars — one bar per day, one segment per series, drawn in the
 * order `series` is given (first at the bottom).
 *
 * Bars, not lines, because the question this answers is "how many users did
 * each day bring, and where did they come from" — a magnitude-per-bucket
 * question. Lines imply a continuous quantity between samples and make a zero
 * day look like a dip in a curve rather than a day that produced nobody.
 *
 * Hovering a day shows every segment plus the day's total.
 */
export function BarChart({ labels, series, height = 260 }: ChartProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)
  const padL = 40
  const padR = 12
  const padT = 12
  const padB = 28
  const width = 800
  const innerW = width - padL - padR
  const innerH = height - padT - padB

  const totals = useMemo(() => {
    const n = labels.length
    const out = new Array(n).fill(0)
    for (const s of series) {
      for (let i = 0; i < n; i++) out[i] += s.values[i] ?? 0
    }
    return out as number[]
  }, [labels.length, series])

  const max = useMemo(() => {
    const m = Math.max(1, ...totals)
    const pow = Math.pow(10, Math.floor(Math.log10(m)))
    const norm = m / pow
    const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10
    return nice * pow
  }, [totals])

  const n = labels.length
  // Bars share the plot width evenly with a small gutter, so a 30-day and a
  // 7-day window both fill the chart.
  const slot = n > 0 ? innerW / n : innerW
  const barW = Math.max(2, slot * 0.72)
  const xFor = (i: number) => padL + slot * i + (slot - barW) / 2
  const yFor = (v: number) => padT + innerH - (v / max) * innerH
  const stride = Math.max(1, Math.ceil(n / 10))
  const yTicks = [0, max * 0.25, max * 0.5, max * 0.75, max].map((v) => Math.round(v))

  return (
    <div className="chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        onMouseLeave={() => setHoverIdx(null)}
        onMouseMove={(e) => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect()
          const xRel = ((e.clientX - rect.left) / rect.width) * width
          if (xRel < padL || xRel > width - padR) {
            setHoverIdx(null)
            return
          }
          const i = Math.floor((xRel - padL) / slot)
          setHoverIdx(Math.min(n - 1, Math.max(0, i)))
        }}
      >
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={padL}
              x2={width - padR}
              y1={yFor(t)}
              y2={yFor(t)}
              stroke="var(--border)"
              strokeDasharray={i === 0 ? '0' : '2 3'}
            />
            <text x={padL - 6} y={yFor(t) + 4} textAnchor="end" fontSize="11" fill="var(--fg-muted)">
              {t}
            </text>
          </g>
        ))}

        {labels.map((label, i) =>
          i % stride === 0 || i === n - 1 ? (
            <text
              key={label + i}
              x={xFor(i) + barW / 2}
              y={height - 8}
              textAnchor="middle"
              fontSize="10"
              fill="var(--fg-muted)"
            >
              {label}
            </text>
          ) : null
        )}

        {labels.map((_, i) => {
          // Stack from the baseline up, skipping zero segments so a 0-height
          // rect never paints a 1px line at the axis.
          let acc = 0
          return (
            <g key={i} opacity={hoverIdx === null || hoverIdx === i ? 1 : 0.55}>
              {series.map((s) => {
                const v = s.values[i] ?? 0
                if (v <= 0) return null
                const y0 = yFor(acc)
                acc += v
                const y1 = yFor(acc)
                return (
                  <rect
                    key={s.key}
                    x={xFor(i)}
                    y={y1}
                    width={barW}
                    height={Math.max(1, y0 - y1)}
                    fill={s.color}
                  />
                )
              })}
            </g>
          )
        })}
      </svg>

      {hoverIdx !== null && (
        <div className="chart-tooltip">
          <div className="chart-tooltip-date">
            {labels[hoverIdx]} · {totals[hoverIdx]} total
          </div>
          {series
            .filter((s) => (s.values[hoverIdx] ?? 0) > 0)
            .map((s) => (
              <div key={s.key} className="chart-tooltip-row">
                <span className="chart-swatch" style={{ background: s.color }} />
                <span>{s.label}</span>
                <span className="tabular">{s.values[hoverIdx]}</span>
              </div>
            ))}
        </div>
      )}

      <div className="chart-legend">
        {series.map((s) => (
          <div key={s.key} className="chart-legend-item">
            <span className="chart-swatch" style={{ background: s.color }} />
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function LineChart({ labels, series, height = 220 }: ChartProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)
  const padL = 40
  const padR = 12
  const padT = 12
  const padB = 28
  const width = 800
  const innerW = width - padL - padR
  const innerH = height - padT - padB

  const max = useMemo(() => {
    const m = Math.max(1, ...series.flatMap((s) => s.values))
    // Round up to a "nice" tick — 1, 2, 5 × 10^n.
    const pow = Math.pow(10, Math.floor(Math.log10(m)))
    const norm = m / pow
    const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10
    return nice * pow
  }, [series])

  const n = labels.length
  const xFor = (i: number) => padL + (n <= 1 ? innerW / 2 : (i * innerW) / (n - 1))
  const yFor = (v: number) => padT + innerH - (v / max) * innerH

  // X-axis tick stride: aim for ~8 labels max.
  const stride = Math.max(1, Math.ceil(n / 8))

  // Y-axis ticks at 0, 25, 50, 75, 100% of max (rounded).
  const yTicks = [0, max * 0.25, max * 0.5, max * 0.75, max].map((v) => Math.round(v))

  return (
    <div className="chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        onMouseLeave={() => setHoverIdx(null)}
        onMouseMove={(e) => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect()
          const xRel = ((e.clientX - rect.left) / rect.width) * width
          if (xRel < padL || xRel > width - padR) {
            setHoverIdx(null)
            return
          }
          const i = Math.round(((xRel - padL) / innerW) * (n - 1))
          setHoverIdx(Math.min(n - 1, Math.max(0, i)))
        }}
      >
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={padL}
              x2={width - padR}
              y1={yFor(t)}
              y2={yFor(t)}
              stroke="var(--border)"
              strokeDasharray={i === 0 ? '0' : '2 3'}
            />
            <text x={padL - 6} y={yFor(t) + 4} textAnchor="end" fontSize="11" fill="var(--fg-muted)">
              {t}
            </text>
          </g>
        ))}

        {labels.map((label, i) =>
          i % stride === 0 || i === n - 1 ? (
            <text
              key={label + i}
              x={xFor(i)}
              y={height - 8}
              textAnchor="middle"
              fontSize="10"
              fill="var(--fg-muted)"
            >
              {label}
            </text>
          ) : null
        )}

        {series.map((s) => {
          const path = s.values
            .map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`)
            .join(' ')
          return (
            <g key={s.key}>
              <path d={path} stroke={s.color} strokeWidth="2" fill="none" strokeLinejoin="round" />
              {s.values.map((v, i) => (
                <circle
                  key={i}
                  cx={xFor(i)}
                  cy={yFor(v)}
                  r={hoverIdx === i ? 4 : 2}
                  fill={s.color}
                />
              ))}
            </g>
          )
        })}

        {hoverIdx !== null && (
          <line
            x1={xFor(hoverIdx)}
            x2={xFor(hoverIdx)}
            y1={padT}
            y2={padT + innerH}
            stroke="var(--border-strong)"
            strokeDasharray="2 2"
          />
        )}
      </svg>

      {hoverIdx !== null && (
        <div className="chart-tooltip">
          <div className="chart-tooltip-date">{labels[hoverIdx]}</div>
          {series.map((s) => (
            <div key={s.key} className="chart-tooltip-row">
              <span className="chart-swatch" style={{ background: s.color }} />
              <span>{s.label}</span>
              <span className="tabular">{s.values[hoverIdx]}</span>
            </div>
          ))}
        </div>
      )}

      <div className="chart-legend">
        {series.map((s) => (
          <div key={s.key} className="chart-legend-item">
            <span className="chart-swatch" style={{ background: s.color }} />
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
